import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type {
  IntegrationProviderId,
  IntegrationProviderLabel,
} from "@app/common/types/providerTypes";

export const PROVIDER_CATEGORIES: IntegrationProviderLabel[] = [
  { id: "calendar", label: "Calendar" },
  { id: "productivity", label: "Productivity" },
  { id: "messaging", label: "Messaging" },
  { id: "crm", label: "CRM" },
  { id: "workflow", label: "Workflow" },
];

interface ProviderConfigContent {
  /** Description that will appear in the side panel list under the provider name (default/user scope) */
  shortDescription: string;
  /** Description that will appear in the main content in the header (will use the short description if this is not defined) */
  headerDescription?: string;
  /** Description that will appear under the "Configuration" heading when the integration is connected */
  configurationDescription?: string;
  /** Description that will appear in the side panel list under the provider name (workspace scope) */
  workspaceShortDescription?: string;
}

export const PROVIDER_CONTENT: {
  [key in IntegrationProviderId]: ProviderConfigContent;
} = {
  // calendars
  google_calendar: {
    shortDescription: `Allow your Read Assistant to join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.google_calendar.displayName} events.`,
  },
  microsoft_graph_calendar: {
    shortDescription: `Allow your Read Assistant to join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_calendar.displayName} events.`,
  },
  zoom_calendar: {
    shortDescription: `Allow your Read Assistant to join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.zoom_calendar.displayName} events.`,
  },

  // productivity apps
  google_email: {
    shortDescription: `Receive Readouts based on your ${INTEGRATION_PROVIDERS_BY_ID.google_email.displayNameShort} inbox.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.google_email.displayNameShort}. Read can access email content from your inbox.`,
  },
  microsoft_graph_email: {
    shortDescription: `Receive Readouts based on your ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_email.displayNameShort} inbox.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_email.displayNameShort}. Read can access email content from your inbox.`,
  },
  microsoft_graph_teams_chat_ingest: {
    shortDescription: `Receive Readouts based on your ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.displayNameShort} chat messages.`,
    headerDescription: `Receive Readouts based on activity in your ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.displayNameShort} chat messages.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.displayNameShort}. Read can analyze your chat messages to generate Readout reports when it discovers themes.`,
  },
  slack_ingest: {
    shortDescription: `Receive Readouts based on your ${INTEGRATION_PROVIDERS_BY_ID.slack_ingest.displayNameShort} channels.`,
    headerDescription: `Receive Readouts based on activity in your ${INTEGRATION_PROVIDERS_BY_ID.slack_ingest.displayNameShort} channels.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.slack_ingest.displayNameShort}. Read can analyze your channels to generate Readout reports when it discovers themes.`,
  },

  // messaging apps
  microsoft_graph_teams_push: {
    shortDescription: `Share and receive meeting summaries through ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_push.displayNameShort}.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_push.displayName}. Read can push meeting summaries to your ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_push.displayNameShort} channels.`,
  },
  slack_bot: {
    shortDescription: `Share and receive meeting summaries through ${INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort}.`,
    configurationDescription: `You have successfully connected Read to ${INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayName}. Read can push meeting summaries to your ${INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort} channels.`,
    workspaceShortDescription: `Allow your Workspace to send and receive Read meeting summaries in ${INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort}.`,
  },

  // workflow apps
  webhooks: {
    shortDescription:
      "Create custom automated workflows with webhooks from your favorite apps.",
  },
  zapier: {
    shortDescription: `Push meeting notes and transcript into other apps using a ${INTEGRATION_PROVIDERS_BY_ID.zapier.displayName} integration.`,
    headerDescription: `Push meeting notes and transcript into other apps using a ${INTEGRATION_PROVIDERS_BY_ID.zapier.displayName} integration.`,
  },

  // CRM apps
  hubspot: {
    shortDescription: `Sync contact and meeting note data between Read and ${INTEGRATION_PROVIDERS_BY_ID.hubspot.displayName}.`,
  },
  salesforce: {
    shortDescription: `Sync contact and meeting note data between Read and ${INTEGRATION_PROVIDERS_BY_ID.salesforce.displayName}.`,
  },
};
